import { message } from 'antd';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from 'store/user/actions';

const DEBUG_ENABLED =
  process.env.REACT_APP_ENVIRONMENT === 'development' ||
  process.env.REACT_APP_ENVIRONMENT === 'test' ||
  process.env.REACT_APP_ENVIRONMENT === 'local' ||
  process.env.REACT_APP_ENVIRONMENT === 'uat';

/**
 * The number of minutes of inactivity before the user is automatically logged out.
 * Defaults to 30 minutes if the environment variable is not set or invalid.
 */
const INACTIVITY_AUTO_LOGOUT_MINUTES =
  Number(process.env.REACT_APP_AUTH_LOGOUT_INACTIVITY_MINUTES) || 30;

if (DEBUG_ENABLED === true) {
  console.log(
    `[InactivityHandler] environment is ${process.env.REACT_APP_ENVIRONMENT}`
  );
  console.log(
    `[InactivityHandler] setting ${INACTIVITY_AUTO_LOGOUT_MINUTES} min`
  );
}

/**
 * DOM events we "listen to" in order to detect activity.
 */
const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
];

/**
 * @summary Custom hook tracking inactivity and forcing logout after certain configured duration has passed.
 * @description
 * This React hook tracks user activity (mouse movements, key presses,
 * clicks, scrolling, and touch events). If the user remains inactive for
 * the configured duration, it automatically logs them out by dispatching
 * the `logoutUser` action.
 *
 * **Behavior**:
 * - Starts a timer when the user is authenticated.
 * - Resets the timer whenever activity events are detected.
 * - Dispatches `logoutUser` after the timeout expires with no activity.
 * - Cleans up event listeners and timers when unmounted or auth status changes.
 *
 * @example
 * function Layout() {
 *   useInactivityHandler();
 *   return <div>...</div>;
 * }
 */
export function useInactivityHandler() {
  const dispatch = useDispatch();

  const userStatus = useSelector(state => state.user.status);
  const timerRef = useRef(null);

  const isAuthenticated = userStatus === 200;

  useEffect(() => {
    if (isAuthenticated === false) return;

    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        message.warning(
          `Αποσύνδεση λόγω αδράνειας ${INACTIVITY_AUTO_LOGOUT_MINUTES} λεπτών.`,
          6
        );
        dispatch(logoutUser());
      }, INACTIVITY_AUTO_LOGOUT_MINUTES * 60 * 1000);
    };

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetTimer, {
        passive: true,
      })
    );
    resetTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [dispatch, isAuthenticated]);
}
